import { useEffect, useRef, useCallback } from 'react';
import { API_BASE } from './apiBase';
import logger from './logger';

/**
 * Subscribes to the backend realtime channel and dispatches each event as a
 * DOM CustomEvent on `window`. Pages can `window.addEventListener('rt:shifts.changed', …)`
 * to invalidate their queries — keeps the API tiny and avoids a global store.
 *
 * Auto-reconnects with backoff. Survives logout (closes cleanly).
 */
export function useRealtime(enabled = true) {
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const closedRef = useRef(false);

  const connect = useCallback(() => {
    if (typeof window === 'undefined' || !enabled) return;
    closedRef.current = false;
    // Build WS URL: same origin as the API, swap http(s) → ws(s).
    const base = API_BASE || window.location.origin;
    const wsUrl = base.replace(/^http/i, 'ws') + '/api/ws';
    let ws;
    try { ws = new WebSocket(wsUrl); } catch (err) { logger.warn('WebSocket construct failed', err); return; }
    wsRef.current = ws;
    ws.onopen = () => { retryRef.current = 0; };
    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (!msg?.type) return;
        window.dispatchEvent(new CustomEvent(`rt:${msg.type}`, { detail: msg.data || {} }));
      } catch (err) { logger.warn('Realtime: malformed message', err); }
    };
    ws.onclose = () => {
      if (closedRef.current) return;
      // Reconnect with capped exponential backoff (1, 2, 4, 8, 15s)
      const delay = Math.min(15000, 1000 * 2 ** retryRef.current);
      retryRef.current = Math.min(retryRef.current + 1, 4);
      setTimeout(() => { if (!closedRef.current) connect(); }, delay);
    };
    ws.onerror = () => { /* let onclose handle reconnect */ };
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    connect();
    return () => {
      closedRef.current = true;
      try { wsRef.current?.close(); } catch (err) { logger.warn('Realtime: close failed', err); }
    };
  }, [connect, enabled]);
}

/**
 * Subscribe to one or more rt:* events. Returns nothing; the callback fires
 * with the event detail payload. Auto-cleans up.
 */
export function useRealtimeEvent(eventTypes, callback) {
  useEffect(() => {
    if (!callback) return undefined;
    const types = Array.isArray(eventTypes) ? eventTypes : [eventTypes];
    const handler = (e) => callback(e.detail, e.type.replace(/^rt:/, ''));
    types.forEach(t => window.addEventListener(`rt:${t}`, handler));
    return () => types.forEach(t => window.removeEventListener(`rt:${t}`, handler));
  }, [eventTypes, callback]);
}
